import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { navEmitter, NavigatePayload } from '@/utils/nav-emitter';

/**
 * 在 React Router Provider 上下文内监听全局导航事件。
 *
 * 收到 navEmitter 的 `navigate` 事件后，调用 React Router 的
 * `useNavigate` 执行实际的 SPA 路由跳转，从而绕过在 Zustand store
 * 等非 React 上下文中无法使用 hooks 的限制。
 *
 * MUST 在 RouterProvider 内部的顶层 Layout 组件（如 MainLayout）中调用，
 * 确保 `useNavigate` 可以正常工作。
 *
 * @example
 * ```tsx
 * // src/layouts/main-layout/index.tsx
 * import { useNavEmitterListener } from '@/hooks/useNavEmitterListener';
 *
 * export default function MainLayout() {
 *   useNavEmitterListener();
 *   // ...
 * }
 * ```
 */
export function useNavEmitterListener() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleNavigate = ({ path, state }: NavigatePayload) => {
      navigate(path, { state });
    };

    navEmitter.on('navigate', handleNavigate);

    return () => {
      navEmitter.off('navigate', handleNavigate);
    };
  }, [navigate]);
}
